import React, { useState } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import './MorphologyProcessor.less';

interface ProcessStats {
  originalPuzzleCount: number;
  totalEntries: number;
  katiePlaceholders: number;
  dateRange: {
    start: string;
    end: string;
  };
  idRange: {
    start: number;
    end: number;
  };
}

const MorphologyProcessor = () => {
  const [file, setFile] = useState<File | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [startingId, setStartingId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [stats, setStats] = useState<ProcessStats | null>(null);
  const [processedData, setProcessedData] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.name.endsWith('.txt')) {
      setFile(droppedFile);
      setError(null);
      setSuccess(false);
    } else {
      setError('Please drop a .txt file');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!startDate) {
      setError('Please enter a start date');
      return;
    }

    if (!startingId || parseInt(startingId) < 1) {
      setError('Please enter a valid starting ID (positive number)');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setStats(null);

      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setError('You must be logged in to use this tool');
        setLoading(false);
        return;
      }

      const token = await user.getIdToken();

      // Read file content
      const fileContent = await file.text();

      // Send to backend
      const response = await axios.post(
        '/api/admin/process-morphology',
        {
          fileContent,
          startDate,
          startingId: parseInt(startingId)
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setStats(response.data.stats);
        setProcessedData(response.data.data);
      } else {
        setError(response.data.error || 'Processing failed');
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error processing morphology data:', err);

      if (err.response?.status === 403) {
        setError('Unauthorized - You do not have admin access');
      } else if (err.response?.data?.error) {
        const errorMsg = err.response.data.error;
        const lineNum = err.response.data.lineNumber;
        setError(lineNum ? `Line ${lineNum}: ${errorMsg}` : errorMsg);
      } else {
        setError('Failed to process morphology data');
      }

      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!processedData) return;

    const dataStr = JSON.stringify(processedData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'morphology-processed.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box className="morphology-processor-container">
      <Box className="content-wrapper">
        <Typography variant="h4" gutterBottom>
          Morphology Puzzle Processor
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Upload a morphology puzzle text file to process it into the final JSON format
        </Typography>

        <Divider sx={{ my: 3 }} />

        <form onSubmit={handleSubmit}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload File
            </Typography>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s',
                cursor: 'pointer',
                mb: 2
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {isDragging ? 'Drop file here' : 'Drag and drop a .txt file here'}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                or
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Choose File
                <input
                  type="file"
                  accept=".txt"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
            </Box>
            {file && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Selected: {file.name}
              </Alert>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuration
            </Typography>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              helperText="First puzzle date (YYYY-MM-DD format)"
            />
            <TextField
              label="Starting ID"
              type="number"
              value={startingId}
              onChange={(e) => setStartingId(e.target.value)}
              fullWidth
              helperText="ID for the first puzzle"
              inputProps={{ min: 1 }}
            />
          </Paper>

          <Box display="flex" justifyContent="center">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading || !file || !startDate || !startingId}
              sx={{ minWidth: 200 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Process Puzzles'}
            </Button>
          </Box>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {success && stats && (
          <Box mt={3}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Processing completed successfully!
            </Alert>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Processing Stats
              </Typography>
              <Typography variant="body2">
                Original puzzles: {stats.originalPuzzleCount}
              </Typography>
              <Typography variant="body2">
                Total entries (with Katie placeholders): {stats.totalEntries}
              </Typography>
              <Typography variant="body2">
                Katie placeholders added: {stats.katiePlaceholders}
              </Typography>
              <Typography variant="body2">
                Date range: {stats.dateRange.start} to {stats.dateRange.end}
              </Typography>
              <Typography variant="body2">
                ID range: {stats.idRange.start} to {stats.idRange.end}
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDownload}
                sx={{ mt: 2 }}
              >
                Download JSON
              </Button>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MorphologyProcessor;
